import React, { useLayoutEffect } from 'react';
import { Websocket } from './services/websocket';
import { AppInitializer } from './services/app-initializer';
import { ensureSessionValidOrRefresh } from './services/ensure-session';
import { useSelector } from 'react-redux';
import store, { AppState } from './stores';
import { updateUser, UserType } from './stores/authenticated';
import { connected, disconnected } from './stores/receiver';
import { InfoType } from './App';
import { find } from 'lodash-es';

interface WebSocketInterface {
  children?: React.ReactElement | null;
}

// 直近に適用した getInfo のサーバー時刻。順序が逆転した古い更新を破棄するために使う
let lastInfoTime = 0;

function bindAppHandlers(socket: ReturnType<typeof Websocket.connect>, userId: number): void {
  const handleConnect = (): void => {
    // 再接続時はサーバー側の時刻基準がリセットされうるため比較値も初期化する
    lastInfoTime = 0;
    socket.emit('getInfo', {});
    store.dispatch(connected());
  };

  socket.off('connect');
  socket.on('connect', handleConnect);

  socket.off('disconnect');
  socket.on('disconnect', (reason: string) => {
    store.dispatch(disconnected());
    if (reason === 'io server disconnect') {
      Websocket.recoverAfterServerDisconnect();
    }
  });

  socket.off('getInfo');
  socket.on('getInfo', ({ data, time }: { data: InfoType; time?: number }) => {
    const eventTime = typeof time === 'number' ? time : undefined;
    if (eventTime !== undefined) {
      if (eventTime <= lastInfoTime) {
        return;
      }
      lastInfoTime = eventTime;
    }
    void (async () => {
      const sessionOk = await ensureSessionValidOrRefresh();
      if (!sessionOk) {
        return;
      }
      if (eventTime !== undefined && eventTime < lastInfoTime) {
        return;
      }
      AppInitializer.initAppState(data);
      store.dispatch(updateUser(find(data.users, { id: userId }) as UserType));
    })();
  });

  socket.off('error');
  socket.on('error', () => {
    // 認証失敗等はサーバーが切断する。詳細は本番ログに任せる
  });

  // force reconnect 後に connect イベントを取り逃した場合の同期
  if (socket.connected) {
    handleConnect();
  }
}

const WebSocket: React.FC<WebSocketInterface> = (props) => {
  const userId = useSelector((state: AppState) => state.authenticated.userId as number);

  // useLayoutEffect: 子の useSocketEvent (useEffect) より先に connect し、リスナー取り逃しを防ぐ
  // accessTokenExpiresAt は依存に含めない。HTTP トークン更新で Socket を貼り直すと
  // handleDisconnect と handleConnection の DB レースで offline 化するため。
  useLayoutEffect(() => {
    if (!userId) {
      Websocket.socket?.disconnect();
      return;
    }

    const attachHandlers = (socket: ReturnType<typeof Websocket.connect>): void => {
      bindAppHandlers(socket, userId);
    };

    Websocket.connect();
    const initial = Websocket.socket;
    if (initial) {
      attachHandlers(initial);
    }

    const unsubscribe = Websocket.onSocketReplaced(attachHandlers);

    return () => {
      unsubscribe();
      const socket = Websocket.socket;
      if (socket) {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('getInfo');
        socket.off('error');
      }
    };
  }, [userId]);

  return <>{props.children}</>;
};

export default WebSocket;
